import moment from "moment";
import * as React from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import Filters, { FilterProps } from "../Filters";
import { tl } from "../../../components/translate";
import { IdSelector } from "../EmployeeMultiSelect";
import { downloadExcel } from "../../../helpers/files";
import useMobileScreen from "../../../hooks/useMobileScreen";
import { notificationAdd } from "../../../actions/notification";
import { employeesSortedSelector } from "../../../reducers/employee";
import useIsReactNativeWebView from "../../../hooks/useIsReactNativeWebView";
import useAttendanceSummary from "../../../hooks/query/useAttendanceSummary";
import useCurrentResourceCentre from "../../../hooks/useCurrentResourceCentre";
import { IAttendanceSummaryRow } from "../../../interfaces/AttendanceInterface";
import * as calFns from "../../../components/Calendar/functions/calendarFunctions";
import { serviceProvidersSortedSelector } from "../../../reducers/serviceProvider";
import List, { EmptyView, ListActions, Menu, MenuItem } from "../../../components/OList";
import { formatDataForExcel, formatHeaderForExcel } from "../../accounts/Reports/helper";

// @ts-expect-error - css
import "../Reports.scss";

// helper functions
const formatMinutes = (minutes: number) => {
  if (!minutes) return "00:00";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
};

const formatHours = (time: string | null) => {
  if (!time) return "-";

  const [hours, minutes] = time.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const formattedHours = hours % 12 || 12;

  return `${formattedHours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")} ${period}`;
};

const formatDateToTime = (time: string | null) => {
  if (!time) return "-";
  return moment(time).format("hh:mm A");
};

const docColumns = () => [
  "Date (AD)",
  "Date (BS)",
  "Name",
  "Type",
  "Shift",
  "First Check In",
  "Last Check Out",
  "Worked Time",
  "Break Time",
  "Overtime",
  "Late",
  "Early Exit",
  "Status",
  "Holiday"
];

// eslint-disable-next-line import/prefer-default-export
export function AttendanceSummaryReport(): React.ReactNode {
  const dispatch = useDispatch();
  const rc = useCurrentResourceCentre();
  const { isRnWebView } = useIsReactNativeWebView();
  const isMobileScreen = useMobileScreen();

  // selectors
  const employees = useSelector(employeesSortedSelector);
  const serviceProviders = useSelector(serviceProvidersSortedSelector);

  const [filters, setFilters] = React.useState<FilterProps>({
    from: calFns.startOfDay(new Date()).toDate(),
    until: new Date(),
    employeeIds: [],
    serviceProviderIds: []
  } as any);

  const { data: reportData = [], isLoading: isRequestLoading } = useAttendanceSummary({
    fromDate: moment(filters.from).format("YYYY-MM-DD"),
    toDate: moment(filters.until).format("YYYY-MM-DD"),
    employeeIds: filters.employeeIds,
    serviceProviderIds: filters.serviceProviderIds,
    enabled: !!rc?.id
  });

  const getStaffName = React.useCallback(
    (row: IAttendanceSummaryRow) => {
      if (row.employeeId) {
        const employee = employees.find((e) => Number(e.id) === Number(row.employeeId));
        return employee ? `${employee.firstName} ${employee.lastName}` : "Unknown Employee";
      }
      if (row.serviceProviderId) {
        const sp = serviceProviders.find((s) => Number(s.id) === Number(row.serviceProviderId));
        return sp ? `${sp.firstName} ${sp.lastName}` : "Unknown Service Provider";
      }
      return "-";
    },
    [employees, serviceProviders]
  );

  const docRowProcessor = React.useCallback(
    (row: IAttendanceSummaryRow) => {
      const name = getStaffName(row);
      const type = row.employeeId ? "Employee" : "Service Provider";

      return [
        row.attendanceDate,
        calFns.bsShortDate(row.attendanceDate),
        name,
        type,
        `${formatHours(row?.shift?.startTime)} - ${formatHours(row?.shift?.endTime)}`,
        formatDateToTime(row.firstCheckIn),
        formatDateToTime(row.lastCheckOut),
        formatMinutes(row.workedMinutes),
        formatMinutes(row.breakMinutes),
        formatMinutes(row.overtimeMinutes),
        row.lateMinutes,
        row.earlyExitMinutes,
        row.status,
        row.holidayName || "-"
      ];
    },
    [getStaffName]
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* filter */}
      <Box sx={{ px: 3, py: 2 }}>
        <Filters
          filters={filters}
          onSetFilters={(newFilters) => setFilters(newFilters)}
          isRequestLoading={isRequestLoading}
          employeeIdSelector={IdSelector.EMPLOYEE}
          showNonUserEmployee
        />
      </Box>

      {/* list */}
      <Box
        className="attendanceSummaryReportList"
        sx={{
          borderTop: 1,
          borderColor: "divider",
          width: "100%",
          overflow: "auto"
        }}
      >
        <Box sx={{ minWidth: isMobileScreen || isRnWebView ? "1100px" : "auto" }}>
          {isRequestLoading && reportData.length === 0 ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <List
              automation="attendanceSummaryReportList"
              data={reportData}
              rowHeight={50}
              defaultSortColumn="attendanceDate"
              defaultSortOrder={-1}
              adjustHeightToContents
              columns={[
                {
                  key: "attendanceDate",
                  label: "Date",
                  formatter: ({ attendanceDate }) => (
                    <Box>
                      <Typography variant="body2">{calFns.bsFullDate(attendanceDate)}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        ({attendanceDate})
                      </Typography>
                    </Box>
                  ),
                  sortable: true
                },
                {
                  key: "name",
                  label: "Name",
                  formatter: (row: IAttendanceSummaryRow) => {
                    const name = getStaffName(row);
                    const type = row.employeeId ? "Employee" : "Service Provider";

                    return (
                      <Box>
                        <Typography variant="body2" fontWeight="500">
                          {name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          ({type})
                        </Typography>
                      </Box>
                    );
                  }
                },
                {
                  key: "shift",
                  label: "Shift",
                  formatter: ({ shift }) => (
                    <Typography variant="body2">
                      {`${formatHours(shift?.startTime)} - ${formatHours(shift?.endTime)}`}
                    </Typography>
                  )
                },
                {
                  key: "firstCheckIn",
                  label: "Check In",
                  formatter: ({ firstCheckIn }) => (
                    <Typography variant="body2">{formatDateToTime(firstCheckIn)}</Typography>
                  )
                },
                {
                  key: "lastCheckOut",
                  label: "Check Out",
                  formatter: ({ lastCheckOut }) => (
                    <Typography variant="body2">{formatDateToTime(lastCheckOut)}</Typography>
                  )
                },
                {
                  key: "workedMinutes",
                  label: "Worked Time",
                  formatter: ({ workedMinutes }) => (
                    <Typography variant="body2">{formatMinutes(workedMinutes)}</Typography>
                  )
                },
                {
                  key: "overtimeMinutes",
                  label: "Overtime",
                  formatter: ({ overtimeMinutes }) => (
                    <Typography variant="body2">{formatMinutes(overtimeMinutes)}</Typography>
                  )
                },
                {
                  key: "lateMinutes",
                  label: "Late",
                  formatter: ({ lateMinutes }) => (
                    <Typography variant="body2">
                      {lateMinutes > 0 ? `${lateMinutes}m` : "-"}
                    </Typography>
                  )
                },
                {
                  key: "status",
                  label: "Status",
                  formatter: ({ status, holidayName }) => (
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ textTransform: "capitalize", fontWeight: "500" }}
                      >
                        {status.replace("_", " ")}
                      </Typography>
                      {holidayName && (
                        <Typography variant="caption" color="primary">
                          {holidayName}
                        </Typography>
                      )}
                    </Box>
                  )
                }
              ]}
            >
              <EmptyView>
                <Box textAlign="center" padding="50px">
                  No attendance records found.
                </Box>
              </EmptyView>
              <ListActions>
                {({ getProcessedData }) => (
                  <Menu>
                    <MenuItem
                      onClick={async () => {
                        try {
                          await downloadExcel(
                            "Attendance Summary Report",
                            formatHeaderForExcel(docColumns()),
                            formatDataForExcel(getProcessedData(), docRowProcessor),
                            rc,
                            {
                              from: filters.from,
                              until: filters.until
                            }
                          );
                        } catch (err) {
                          dispatch(
                            notificationAdd({
                              id: new Date().getUTCMilliseconds(),
                              variant: "error",
                              message: "Failed to download Excel.",
                              autoTimeout: true
                            })
                          );
                        }
                      }}
                    >
                      {tl("reports.excel")}
                    </MenuItem>
                  </Menu>
                )}
              </ListActions>
            </List>
          )}
        </Box>
      </Box>
    </Box>
  );
}
